import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ExternalLink, Tag, Filter, ShoppingCart, ArrowRight, Eye } from 'lucide-react';
import { API_URLS, API_CONFIG } from '../../config/api';
import { normalizeUrl } from '../../utils/format';

// Hover scroll effect styles
const customStyles = `
  .group:hover .group-hover\\:object-bottom {
    transition: all 20s ease;
  }
  .image-short { transition: all 3s ease !important; }
  .image-medium { transition: all 8s ease !important; }
  .image-long { transition: all 15s ease !important; }
  .image-very-long { transition: all 25s ease !important; }
  /* Web sitesi tasarımları hero input placeholder siyah */
  .web-design-hero-section input::placeholder {
    color: black !important;
  }
`;

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

function TemplateCard({ item, categories }) {
  const BASE_URL = API_CONFIG.BASE_URL;
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/admin_api.php')) {
      const m = url.match(/getScreenshot&file=([^&]+)$/);
      if (m && m[1]) return `${BASE_URL}/backend/uploads/${m[1]}`;
      return `${BASE_URL}/backend${url}`;
    }
    return `${BASE_URL}${url}`;
  };
  
  // Get category name from category
  const getCategoryName = () => {
    return item.category || null;
  };
  
  // Parse tags if it's a JSON string
  const getTags = () => {
    if (!item.tags) return [];
    if (typeof item.tags === 'string') {
      try {
        return JSON.parse(item.tags);
      } catch {
        return item.tags.split(',').map(t => t.trim());
      }
    }
    if (Array.isArray(item.tags)) return item.tags;
    return [];
  };

  const hasPrice = item.price !== null && item.price !== undefined && item.price !== '';

  const tags = getTags();
  const hasTags = tags.length > 0;
  const hasPurchase = !!item.purchase_url;
  const hasDemo = !!item.demo_url;
  const hasAdmin = !!item.admin_url;
  const hasAnyActions = hasPurchase || hasDemo || hasAdmin;
  
  return (
    <div className="group relative rounded-xl border border-purple-200 overflow-hidden hover:shadow-xl hover:border-purple-400 transition-all duration-300 bg-white">
      <Badge className="absolute top-4 right-4 z-10 bg-purple-600 text-white shadow-md uppercase tracking-wide text-[10px]">
        Ömür Boyu Lisans
      </Badge>
      <div className="aspect-[16/10] bg-gradient-to-br from-purple-50 to-gray-100 overflow-hidden">
        <img
          src={normalizeUrl(item.image_url || item.image)}
          alt={item.name || item.title}
          className="w-full h-full object-cover object-top transition-all duration-[20s] ease group-hover:object-bottom"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
          onLoad={(e) => {
            const nh = e.target.naturalHeight;
            const cls = getImageTransitionClass(nh);
            if (cls) e.target.classList.add(cls);
          }}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-900">{item.name || item.title}</h3>
          {getCategoryName() && (
            <Badge className="bg-purple-100 text-purple-700 text-xs">
              {getCategoryName()}
            </Badge>
          )}
        </div>
        
        {(item.description || item.desc) && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.description || item.desc}</p>
        )}
        
        {/* Tags */}
        {hasTags && (
          <div className="flex flex-wrap gap-1.5 mt-3 mb-3">
            {tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-xs border-purple-200 text-purple-600 bg-purple-50">
                {tag}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Action Buttons */}
        {/* Tasarımı İncele Button - Always visible if slug exists */}
        {item.slug && (
          <Link to={`/hazir-web-sitesi-tasarimlari/${item.slug}`} className="block">
            <Button
              size="sm"
              className="w-full font-semibold transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white mt-4 mb-2"
            >
              <Eye className="w-4 h-4 mr-2" />
              <span>Tasarımı İncele</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        )}
        
        {hasPurchase && (
          <Button
            size="sm"
            onClick={() => window.open(item.purchase_url, '_blank')}
            className="w-full font-semibold transition-all duration-300 transform hover:scale-105 bg-green-600 hover:bg-green-700 text-white mb-2"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            <span>Satın Al</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        )}
        
        {(hasDemo || hasAdmin) && (
          <div className="mt-2 flex gap-2">
            {hasDemo && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const normalizedUrl = normalizeUrl(item.demo_url);
                  window.open(normalizedUrl, '_blank');
                }}
                className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Demo
              </Button>
            )}
            {hasAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(item.admin_url, '_blank')}
                className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Admin
              </Button>
            )}
          </div>
        )}
        
        {/* Price - Below buttons */}
        {hasPrice && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-center">
              <span className="text-lg font-bold text-purple-600">
                {formatPrice(item.price)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HazirWebSitesiTasarimlari() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tplRes, catRes] = await Promise.all([
          fetch(API_URLS.ADMIN.WEBSITE_TEMPLATES),
          fetch(API_URLS.ADMIN.WEBSITE_TEMPLATE_CATEGORIES)
        ]);
        const tplData = await tplRes.json();
        const catData = await catRes.json();
        
        // Handle both wrapped and direct array responses
        const templatesList = Array.isArray(tplData) ? tplData : (tplData?.data || []);
        const categoriesList = Array.isArray(catData) ? catData : (catData?.data || []);
        
        if (Array.isArray(templatesList) && templatesList.length > 0) {
          setTemplates(templatesList);
          
          // Extract all unique tags
          const tagsSet = new Set();
          templatesList.forEach(template => {
            if (template.tags) {
              let tags = [];
              if (typeof template.tags === 'string') {
                try {
                  tags = JSON.parse(template.tags);
                } catch {
                  tags = template.tags.split(',').map(t => t.trim());
                }
              } else if (Array.isArray(template.tags)) {
                tags = template.tags;
              }
              tags.forEach(tag => tagsSet.add(tag));
            }
          });
          setAllTags(Array.from(tagsSet));
        }
        
        if (Array.isArray(categoriesList) && categoriesList.length > 0) {
          setCategories(categoriesList.filter(c => c.is_active !== false));
        } else if (Array.isArray(templatesList)) {
          // Extract categories from templates if no category API
          const fromTpl = Array.from(new Set(templatesList.map(t => t.category).filter(Boolean)))
            .map(n => ({ name: n, slug: String(n).toLowerCase().replace(/\s+/g,'-') }));
          setCategories(fromTpl);
        }
      } catch (e) {
        console.error('Error fetching templates:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Build hierarchical category tree
  const buildCategoryTree = (categories) => {
    const categoryMap = {};
    const rootCategories = [];
    
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    
    categories.forEach(cat => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else {
        rootCategories.push(categoryMap[cat.id]);
      }
    });
    
    return rootCategories;
  };

  // Helper function to get all descendant category IDs (including the category itself)
  const getCategoryAndDescendants = (categoryId) => {
    const result = [categoryId];
    const findChildren = (parentId) => {
      categories.forEach(cat => {
        if (cat.parent_id === parentId) {
          if (!result.includes(cat.id)) {
            result.push(cat.id);
            findChildren(cat.id);
          }
        }
      });
    };
    findChildren(categoryId);
    return result;
  };

  // Filter templates - Kategoriler ve tag'ler ayrı çalışır (OR mantığı)
  const filteredTemplates = templates.filter(template => {
    // Category filter - check both category_id, including parent/child relationships
    const categoryMatch = selectedCategory === 'all' || (() => {
      if (selectedCategory === 'all') return true;
      
      // If categories have id field, match by id including parent/child relationships
      if (categories.length > 0 && categories[0].id) {
        const selectedCat = categories.find(cat => cat.id === selectedCategory);
        
        if (!selectedCat) {
          // Fallback to name/slug matching
          const tCat = template.category || '';
          const tSlug = String(tCat).toLowerCase().replace(/\s+/g,'-');
          return tCat === selectedCategory || tSlug === selectedCategory;
        }
        
        // Get all category IDs to match (selected category + all its descendants)
        const matchingCategoryIds = getCategoryAndDescendants(selectedCat.id);
        // Get all category names and slugs to match
        const matchingCategories = [];
        matchingCategoryIds.forEach(catId => {
          const cat = categories.find(c => c.id === catId);
          if (cat) {
            matchingCategories.push(cat.name);
            if (cat.slug) matchingCategories.push(cat.slug);
          }
        });
        
        // Check if template's category_id matches
        const templateCategoryId = template.category_id || '';
        if (templateCategoryId && matchingCategoryIds.includes(templateCategoryId)) {
          return true;
        }
        
        // Check if template's category (VARCHAR field) matches category name or slug
        const templateCategory = template.category || '';
        if (templateCategory && matchingCategories.some(catName => {
          const normalized = String(templateCategory).toLowerCase().trim();
          const normalizedCatName = String(catName).toLowerCase().trim();
          const normalizedSlug = String(templateCategory).toLowerCase().replace(/\s+/g,'-').trim();
          return normalized === normalizedCatName || normalizedSlug === normalizedCatName;
        })) {
          return true;
        }
        
        return false;
      } else {
        // Fallback to name/slug matching
        const tCat = template.category || '';
        const tSlug = String(tCat).toLowerCase().replace(/\s+/g,'-');
        return tCat === selectedCategory || tSlug === selectedCategory;
      }
    })();
    
    // Tag filter
    let tagMatch = selectedTag === 'all';
    if (!tagMatch) {
      // Check if template has the selected tag
      let templateTags = [];
      if (template.tags) {
        if (typeof template.tags === 'string') {
          try {
            templateTags = JSON.parse(template.tags);
          } catch {
            templateTags = template.tags.split(',').map(t => t.trim());
          }
        } else if (Array.isArray(template.tags)) {
          templateTags = template.tags;
        }
      }
      tagMatch = templateTags.includes(selectedTag);
    }
    
    // OR mantığı: Kategori VEYA tag eşleşirse göster
    // Eğer her ikisi de "all" değilse, en az biri eşleşmeli
    if (selectedCategory !== 'all' && selectedTag !== 'all') {
      return categoryMatch || tagMatch;
    }
    
    // Eğer biri "all" ise, diğerine göre filtrele
    if (selectedCategory !== 'all') {
      return categoryMatch;
    }
    
    if (selectedTag !== 'all') {
      return tagMatch;
    }
    
    // Her ikisi de "all" ise, hepsini göster
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <style>{customStyles}</style>
      
      {/* Hero */}
      <section 
        className="web-design-hero-section relative bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 py-16 overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
              Hazır Web Sitesi Tasarımları
            </h1>
            <p className="mt-4 text-xl text-purple-100 max-w-3xl mx-auto">
              Alanınıza ve ihtiyacınıza uygun, özenle hazırlanmış modern ve profesyonel görünümlü hazır tasarımlar arasından dilediğinizi seçin.
            </p>
            <div className="mt-6 flex items-center justify-center gap-4 text-purple-100">
              <div className="flex items-center">
                <Filter className="w-5 h-5 mr-2" />
                <span>{filteredTemplates.length} Tasarım</span>
              </div>
              <div className="flex items-center">
                <Tag className="w-5 h-5 mr-2" />
                <span>{allTags.length} Etiket</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section 
        className="py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in 0.2s' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Desktop Only */}
            <aside className="hidden lg:block lg:w-64 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Categories */}
                <Card className="border-purple-200 shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Kategoriler</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => setSelectedCategory('all')}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-all ${
                          selectedCategory === 'all'
                            ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-200'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="font-medium">Tümü</span>
                        <Badge variant="secondary" className="bg-white">
                          {templates.length}
                        </Badge>
                      </button>
                      
                      {(() => {
                        const categoryTree = buildCategoryTree(categories);
                        
                        const renderCategory = (category, level = 0) => {
                          const matchingCategoryIds = getCategoryAndDescendants(category.id);
                          // Get all category names and slugs to match
                          const matchingCategories = [];
                          matchingCategoryIds.forEach(catId => {
                            const cat = categories.find(c => c.id === catId);
                            if (cat) {
                              matchingCategories.push(cat.name);
                              if (cat.slug) matchingCategories.push(cat.slug);
                            }
                          });
                          
                          const count = templates.filter(t => {
                            // Check by category_id
                            if (t.category_id && matchingCategoryIds.includes(t.category_id)) {
                              return true;
                            }
                            
                            // Check by category name or slug (old format)
                            const tCat = t.category || '';
                            if (tCat && matchingCategories.some(catName => {
                              const normalized = String(tCat).toLowerCase().trim();
                              const normalizedCatName = String(catName).toLowerCase().trim();
                              const normalizedSlug = String(tCat).toLowerCase().replace(/\s+/g,'-').trim();
                              return normalized === normalizedCatName || normalizedSlug === normalizedCatName;
                            })) {
                              return true;
                            }
                            
                            return false;
                          }).length;
                          const hasChildren = category.children && category.children.length > 0;
                          
                          return (
                            <div key={category.id}>
                              <button
                                onClick={() => setSelectedCategory(category.id)}
                                className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-all text-left ${
                                  selectedCategory == category.id
                                    ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-200'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                                style={{ paddingLeft: `${16 + level * 20}px` }}
                              >
                                <span className={`font-medium ${level > 0 ? 'text-sm' : ''}`}>
                                  {level > 0 && <span className="mr-2">└</span>}
                                  {category.name}
                                </span>
                                <Badge variant="secondary" className="bg-white">
                                  {count}
                                </Badge>
                              </button>
                              {hasChildren && (
                                <div className="mt-1">
                                  {category.children.map(child => renderCategory(child, level + 1))}
                                </div>
                              )}
                            </div>
                          );
                        };
                        
                        return categoryTree.map(cat => renderCategory(cat));
                      })()}
                    </div>
                  </CardContent>
                </Card>

                {/* Tags */}
                {allTags.length > 0 && (
                  <Card className="border-purple-200 shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Tag className="w-5 h-5 mr-2" />
                        Etiketler
                      </h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => setSelectedTag('all')}
                          className={`w-full px-3 py-1.5 rounded-lg text-sm transition-all ${
                            selectedTag === 'all'
                              ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-200 font-medium'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Tümü
                        </button>
                        
                        {allTags.map((tag, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedTag(tag)}
                            className={`w-full px-3 py-1.5 rounded-lg text-sm transition-all ${
                              selectedTag === tag
                                ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-200 font-medium'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </aside>

            {/* Mobile Filters - Wrap Layout */}
            <div className="lg:hidden space-y-4 mb-6">
              {/* Mobile Categories */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1">Kategoriler</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>Tümü</span>
                    <Badge variant="secondary" className={selectedCategory === 'all' ? 'bg-white text-purple-600' : 'bg-white'}>
                      {templates.length}
                    </Badge>
                  </button>
                  
                  {(() => {
                    const categoryTree = buildCategoryTree(categories);
                    
                    const renderCategory = (category, level = 0) => {
                      const matchingCategoryIds = getCategoryAndDescendants(category.id);
                      const count = templates.filter(t => {
                        if (t.category_id) {
                          return matchingCategoryIds.includes(t.category_id);
                        } else {
                          // Fallback for old format
                          const tCat = t.category || '';
                          const tSlug = String(tCat).toLowerCase().replace(/\s+/g,'-');
                          return tCat === category.name || tSlug === (category.slug || category.name);
                        }
                      }).length;
                      const hasChildren = category.children && category.children.length > 0;
                      
                      return (
                        <React.Fragment key={category.id}>
                          <button
                            onClick={() => setSelectedCategory(category.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                              selectedCategory == category.id
                                ? 'bg-purple-600 text-white shadow-md'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            style={{ marginLeft: `${level * 20}px` }}
                          >
                            {level > 0 && <span className="mr-1">└</span>}
                            <span className={level > 0 ? 'text-xs' : ''}>{category.name}</span>
                            <Badge variant="secondary" className={selectedCategory == category.id ? 'bg-white text-purple-600' : 'bg-white'}>
                              {count}
                            </Badge>
                          </button>
                          {hasChildren && category.children.map(child => renderCategory(child, level + 1))}
                        </React.Fragment>
                      );
                    };
                    
                    return categoryTree.map(cat => renderCategory(cat));
                  })()}
                </div>
              </div>

              {/* Mobile Tags */}
              {allTags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center">
                    <Tag className="w-4 h-4 mr-1" />
                    Etiketler
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedTag('all')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedTag === 'all'
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Tümü
                    </button>
                    
                    {allTags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedTag(tag)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                          selectedTag === tag
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Main Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                    <p className="text-gray-600">Tasarımlar yükleniyor...</p>
                  </div>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <Card className="p-12 text-center">
                  <div className="text-gray-400 mb-4">
                    <Filter className="w-16 h-16 mx-auto" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Tasarım bulunamadı
                  </h3>
                  <p className="text-gray-500">
                    Seçili filtrelerle eşleşen tasarım bulunmuyor.
                  </p>
                </Card>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {filteredTemplates.length} Tasarım Bulundu
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredTemplates.map((item, idx) => (
                      <TemplateCard key={item.id || idx} item={item} categories={categories} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
